import type { XrefPumps } from "@/composables/forms/formTypes";
import { getNominalPressureHeadFt } from "@/libs/calculations/pumps";
import { getPowerPerStageBHP } from "./stages";
import { getPeakHeadPointForPumpFt } from "./peakSearch";

export const getPressurePerStageHeadFt = (
  flowRateSTBD: number,
  xrefPump: XrefPumps[number],
  gasHeadRerateRatio: number,
  frequencyRatio: number,
  pumpWearHeadCorrectionRatio: number
): number => {
  // Apply pump wear head correction for each stage
  return (
    getNominalPressureHeadFt(
      flowRateSTBD,
      xrefPump.r_pump?.hb,
      xrefPump.r_pump?.h1,
      xrefPump.r_pump?.h2,
      xrefPump.r_pump?.h3,
      xrefPump.r_pump?.h4,
      xrefPump.r_pump?.h5
    ) *
    xrefPump.head_correction_ratio *
    gasHeadRerateRatio *
    frequencyRatio ** 2 *
    pumpWearHeadCorrectionRatio
  );
};

export const getProducedHeadForPumpAtFlowRateFt = (
  flowRateBBLD: number,
  xrefPump: XrefPumps[number],
  gasHeadRerateRatio: number,
  frequencyRatio: number,
  pumpWearHeadCorrectionRatio: number
): number => {
  const pump = xrefPump.r_pump;
  if (!pump || flowRateBBLD <= 0 || frequencyRatio <= 0) {
    return 0;
  }

  const rateCorrectionRatio = xrefPump.rate_correction_ratio || 1;
  const totalFlowRateCorrectedSTBD =
    (1 / rateCorrectionRatio) * (flowRateBBLD / frequencyRatio);
  const pumpGraphMax = pump.max_graph_rate_standard_pump_bpd || 0;
  const invalidPumpIntakeFlowRate =
    totalFlowRateCorrectedSTBD > pumpGraphMax ||
    totalFlowRateCorrectedSTBD <= 0;

  if (invalidPumpIntakeFlowRate) {
    return 0;
  }

  const headFtPerStage = getPressurePerStageHeadFt(
    totalFlowRateCorrectedSTBD,
    xrefPump,
    gasHeadRerateRatio,
    frequencyRatio,
    pumpWearHeadCorrectionRatio
  );

  return headFtPerStage * (xrefPump.total_stages || 1);
};

export const getProducedBrakeHorsepowerForPumpAtFlowRateHP = (
  flowRateBBLD: number,
  xrefPump: XrefPumps[number],
  frequencyRatio: number,
  fluidDensitySpecificGravity: number,
  pumpWearBHPCorrectionRatio: number
): number => {
  const pump = xrefPump.r_pump;
  if (
    !pump ||
    flowRateBBLD <= 0 ||
    frequencyRatio <= 0 ||
    fluidDensitySpecificGravity <= 0
  ) {
    return 0;
  }

  const rateCorrectionRatio = xrefPump.rate_correction_ratio || 1;
  const totalFlowRateCorrectedSTBD =
    (1 / rateCorrectionRatio) * (flowRateBBLD / frequencyRatio);
  const pumpGraphMax = pump.max_graph_rate_standard_pump_bpd || 0;
  const invalidPumpIntakeFlowRate =
    totalFlowRateCorrectedSTBD > pumpGraphMax ||
    totalFlowRateCorrectedSTBD <= 0;

  if (invalidPumpIntakeFlowRate) {
    return 0;
  }

  const bhpPerStageHP = getPowerPerStageBHP(
    totalFlowRateCorrectedSTBD,
    xrefPump,
    frequencyRatio,
    fluidDensitySpecificGravity,
    pumpWearBHPCorrectionRatio
  );

  return bhpPerStageHP * (xrefPump.total_stages || 1);
};

export const getMaxHeadForPumpFt = (
  xrefPump: XrefPumps[number],
  gasHeadRerateRatio: number,
  frequencyRatio: number,
  pumpWearHeadCorrectionRatio: number
): number => {
  return getPeakHeadPointForPumpFt(
    xrefPump,
    gasHeadRerateRatio,
    frequencyRatio,
    pumpWearHeadCorrectionRatio
  ).headFeet;
};
